import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  ImageSourcePropType,
  Platform,
} from 'react-native';
import PlatformIcon from '../common/PlatformIcon';

// 直接定义颜色，避免 @cubemeet/core 导入问题
const MODULE_COLOR = '#F97316'; // travel 模块的橙色

// ============ 类型定义 ============

export interface ReceiptItem {
  /** 收据ID */
  id: string;
  /** 标题 */
  title: string;
  /** 金额 */
  amount: number;
  /** 货币 */
  currency: string;
  /** 汇率 (可选) */
  exchangeRate?: number;
  /** 类别 */
  category?: string;
  /** 上传者信息 */
  uploader?: {
    id?: string | null;
    name?: string | null;
    avatarUrl?: string | null;
  } | null;
  /** 上传日期 */
  uploadDate?: string | null;
  /** 状态: pending / approved / rejected */
  status?: 'pending' | 'approved' | 'rejected' | string;
}

export interface ReceiptListSectionProps {
  /** 收据列表 */
  receipts: ReceiptItem[];
  /** 货币符号 */
  currency?: string;
  /** 点击上传按钮 */
  onUploadPress?: () => void;
  /** 点击单个收据 */
  onReceiptPress?: (receipt: ReceiptItem) => void;
  /** 格式化金额 */
  formatCurrency?: (amount: number, currency?: string) => string;
  /** 获取头像 source */
  getAvatarSource?: (url: string) => ImageSourcePropType | null;
}

// ============ 辅助函数 ============

const defaultFormatCurrency = (amount: number, currency?: string): string => {
  const symbol = currency || 'HK$';
  return `${symbol}${amount.toFixed(2)}`;
};

const formatDate = (dateString?: string | null): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return `${date.getFullYear()}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}`;
  } catch {
    return String(dateString);
  }
};

const getCategoryIcon = (category?: string): string => {
  const iconMap: Record<string, string> = {
    food: 'restaurant',
    transport: 'directions-car',
    accommodation: 'hotel',
    activities: 'local-activity',
    shopping: 'shopping-bag',
    general: 'receipt',
  };
  return iconMap[category || ''] || 'receipt';
};

const getStatusInfo = (status?: string) => {
  switch (status) {
    case 'approved':
      return { backgroundColor: '#ECFDF5', color: '#059669', text: 'Approved' };
    case 'rejected':
      return { backgroundColor: '#FEF2F2', color: '#DC2626', text: 'Rejected' };
    case 'pending':
    default:
      return { backgroundColor: '#FEF3C7', color: '#D97706', text: 'Pending' };
  }
};

// ============ 组件 ============

const ReceiptListSection: React.FC<ReceiptListSectionProps> = ({
  receipts,
  onUploadPress,
  onReceiptPress,
  formatCurrency = defaultFormatCurrency,
  getAvatarSource,
}) => {
  const renderAvatar = (uploader?: ReceiptItem['uploader']) => {
    if (!uploader) {
      return (
        <View style={[styles.avatar, styles.avatarPlaceholder]}>
          <Text style={styles.avatarText}>?</Text>
        </View>
      );
    }

    const initial = (uploader.name || '?').charAt(0).toUpperCase();

    if (uploader.avatarUrl && getAvatarSource) {
      const source = getAvatarSource(uploader.avatarUrl);
      if (source) {
        return <Image source={source} style={styles.avatar} />;
      }
    }

    return (
      <View style={[styles.avatar, styles.avatarPlaceholder]}>
        <Text style={styles.avatarText}>{initial}</Text>
      </View>
    );
  };

  return (
    <View style={styles.container}>
      {/* 标题行 */}
      <View style={styles.header}>
        <View style={styles.titleRow}>
          <PlatformIcon name="receipt" size={20} color={MODULE_COLOR} family="MaterialIcons" />
          <Text style={styles.title}>收据</Text>
        </View>
        {onUploadPress && (
          <TouchableOpacity style={styles.uploadButton} onPress={onUploadPress}>
            <PlatformIcon name="cloud-upload" size={20} color="#FFFFFF" family="MaterialIcons" />
          </TouchableOpacity>
        )}
      </View>

      {/* 收据列表 */}
      {receipts.length === 0 ? (
        <View style={styles.emptyState}>
          <PlatformIcon name="receipt-long" size={48} color="#D1D5DB" family="MaterialIcons" />
          <Text style={styles.emptyText}>暂无收据</Text>
        </View>
      ) : (
        receipts.map((receipt) => {
          const statusInfo = getStatusInfo(receipt.status);
          
          return (
            <TouchableOpacity
              key={receipt.id}
              style={styles.receiptCard}
              onPress={() => onReceiptPress?.(receipt)}
              activeOpacity={0.7}
            >
              {/* 顶部：类别图标 + 标题 | 金额 */}
              <View style={styles.receiptTop}>
                <View style={styles.receiptLeft}>
                  <View style={styles.categoryIcon}>
                    <PlatformIcon 
                      name={getCategoryIcon(receipt.category)} 
                      size={18} 
                      color={MODULE_COLOR} 
                      family="MaterialIcons" 
                    />
                  </View>
                  <Text style={styles.receiptTitle} numberOfLines={1}>{receipt.title}</Text>
                </View>
                <View style={styles.receiptRight}>
                  <Text style={styles.receiptAmount}>
                    {formatCurrency(receipt.amount, receipt.currency)}
                  </Text>
                  {receipt.exchangeRate && (
                    <Text style={styles.exchangeRate}>汇率: {receipt.exchangeRate.toFixed(4)}</Text>
                  )}
                </View>
              </View>

              {/* 底部：上传者 | 状态 */}
              <View style={styles.receiptBottom}>
                <View style={styles.uploaderRow}>
                  {renderAvatar(receipt.uploader)}
                  <View style={styles.uploaderInfo}>
                    <Text style={styles.uploaderName} numberOfLines={1}>
                      {receipt.uploader?.name || '未知用户'}
                    </Text>
                    <Text style={styles.uploadDate}>{formatDate(receipt.uploadDate)}</Text>
                  </View>
                </View>
                <View style={styles.statusRow}>
                  <PlatformIcon name="description" size={16} color={MODULE_COLOR} family="MaterialIcons" />
                  <View style={[styles.statusBadge, { backgroundColor: statusInfo.backgroundColor }]}>
                    <Text style={[styles.statusText, { color: statusInfo.color }]}>
                      {statusInfo.text}
                    </Text>
                  </View>
                </View>
              </View>
            </TouchableOpacity>
          );
        })
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    ...Platform.select({
      web: {
        boxShadow: '0px 2px 8px rgba(0,0,0,0.06)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.06,
        shadowRadius: 8,
        elevation: 2,
      },
    }),
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  uploadButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: MODULE_COLOR,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 32,
  },
  emptyText: {
    marginTop: 12,
    fontSize: 14,
    color: '#9CA3AF',
  },
  // 收据卡片
  receiptCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 14,
    marginBottom: 12,
  },
  receiptTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  receiptLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  categoryIcon: {
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: '#FFF7ED',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  receiptTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: '#111827',
    flex: 1,
  },
  receiptRight: {
    alignItems: 'flex-end',
    marginLeft: 12,
  },
  receiptAmount: {
    fontSize: 17,
    fontWeight: '700',
    color: MODULE_COLOR,
  },
  exchangeRate: {
    fontSize: 11,
    color: '#9CA3AF',
    marginTop: 2,
  },
  receiptBottom: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  uploaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    marginRight: 8,
  },
  avatarPlaceholder: {
    backgroundColor: MODULE_COLOR,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '600',
  },
  uploaderInfo: {
    flex: 1,
  },
  uploaderName: {
    fontSize: 13,
    fontWeight: '500',
    color: '#374151',
  },
  uploadDate: {
    fontSize: 11,
    color: '#9CA3AF',
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  statusBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
  },
});

export default ReceiptListSection;
